import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Info, Save, UserPlus } from "lucide-react";
import NavBar from "./Navbar";
import TopNavBar from "./TopNavbar";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  createAccount,
  fetchAccountById,
  updateAccount,
  fetchMyProfile,
  updateMyProfile,
} from "../services/AccountService";
import { ROLES, formatAccountId, roleLabel, PERMISSIONS } from "../utils/rbac";
import { useRoutePermissionGuard } from "../hooks/usePermissionGuard";
import "../styles/AccountForm.css";

function formatDisplayDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatLastLoginDisplay(value) {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );
  const startOfThatDay = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  const dayDiff = Math.round(
    (startOfToday.getTime() - startOfThatDay.getTime()) / (1000 * 60 * 60 * 24),
  );

  const timeStr = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  if (dayDiff === 0) return `Today, ${timeStr}`;
  if (dayDiff === 1) return "Yesterday";
  return formatDisplayDate(value);
}

const inputClass =
  "w-full max-w-full box-border py-3 px-4 border-0 rounded-lg text-sm outline-none bg-[#F3F4F6] text-[#1B1C1C] placeholder:text-[#9CA3AF] focus:ring-2 focus:ring-[#2F7F57]/20";

function AccountForm() {
  const { accountId: accountIdParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isSelfProfileRoute = location.pathname === "/myAccount";
  const isEditMode = isSelfProfileRoute || Boolean(accountIdParam);
  const { allowed: canManage, PermissionDialog } = useRoutePermissionGuard(
    isSelfProfileRoute ? null : PERMISSIONS.MANAGE_ACCOUNTS,
    "manage accounts",
  );
  const formEnabled = canManage || isSelfProfileRoute;

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [accountMeta, setAccountMeta] = useState(null);

  const [fullname, setFullname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [role, setRole] = useState(ROLES.STAFF);
  const [status, setStatus] = useState("active");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [pendingStatus, setPendingStatus] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);

  const currentAccountId = localStorage.getItem("account_id");
  const isEditingSelf =
    isSelfProfileRoute ||
    (isEditMode &&
      currentAccountId != null &&
      String(currentAccountId) === String(accountIdParam));

  useEffect(() => {
    if (!isEditMode) return undefined;

    const loadAccount = async () => {
      setLoading(true);
      try {
        const res = isSelfProfileRoute
          ? await fetchMyProfile()
          : await fetchAccountById(accountIdParam);
        if (res?.status !== 1 || !res.data) {
          throw new Error(res?.message || "Account not found");
        }
        const account = res.data;
        setAccountMeta(account);
        setFullname(account.fullname || "");
        setEmail(account.email || "");
        setRole(account.role || ROLES.STAFF);
        setStatus(account.status || "active");
      } catch (err) {
        toast.error(
          err?.response?.data?.message ||
            err.message ||
            "Failed to load account",
        );
        navigate(isSelfProfileRoute ? "/dashboard" : "/manageAccounts");
      } finally {
        setLoading(false);
      }
    };

    loadAccount();
  }, [accountIdParam, isEditMode, isSelfProfileRoute, navigate]);

  const handleStatusTabChange = (nextStatus) => {
    if (isEditingSelf || nextStatus === status) return;

    if (!isEditMode) {
      setStatus(nextStatus);
      return;
    }

    setPendingStatus(nextStatus);
    setStatusDialogOpen(true);
  };

  const confirmStatusChange = () => {
    if (pendingStatus) setStatus(pendingStatus);
    setPendingStatus(null);
    setStatusDialogOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!fullname.trim()) {
      toast.error("Full name is required");
      return;
    }
    if (!email.trim()) {
      toast.error("Email is required");
      return;
    }

    if (!isEditMode) {
      if (!password.trim()) {
        toast.error("Password is required");
        return;
      }
      if (password !== confirmPassword) {
        toast.error("Passwords do not match");
        return;
      }
    }

    if (isEditMode) {
      if (isEditingSelf && password.trim()) {
        if (password !== confirmPassword) {
          toast.error("Passwords do not match");
          return;
        }
      }

      setSaving(true);
      try {
        const payload = {
          fullname: fullname.trim(),
          email: email.trim(),
        };
        if (!isEditingSelf) {
          payload.role = role;
          payload.status = status;
        }
        if (isEditingSelf && password.trim()) {
          payload.password = password;
        }
        const res = isSelfProfileRoute
          ? await updateMyProfile(payload)
          : await updateAccount(accountIdParam, payload);
        if (res?.status !== 1) throw new Error(res?.message || "Update failed");
        toast.success("Account updated successfully");
        if (res?.data?.fullname) {
          localStorage.setItem("user_name", res.data.fullname);
        }
        if (res?.data?.email) {
          localStorage.setItem("user_email", res.data.email);
        }
        window.dispatchEvent(new Event("user-session-updated"));
        navigate(isSelfProfileRoute ? "/dashboard" : "/manageAccounts");
      } catch (err) {
        toast.error(
          err?.response?.data?.message ||
            err.message ||
            "Failed to save account",
        );
      } finally {
        setSaving(false);
      }
      return;
    }

    setSaving(true);
    try {
      const payload = {
        fullname: fullname.trim(),
        email: email.trim(),
        password,
        role,
        status,
      };
      const res = await createAccount(payload);
      if (res?.status !== 1) throw new Error(res?.message || "Create failed");
      toast.success("Account created successfully");

      navigate("/manageAccounts");
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err.message || "Failed to save account",
      );
    } finally {
      setSaving(false);
    }
  };

  const pageTitle = isSelfProfileRoute
    ? "My Account"
    : isEditMode
      ? "Edit Account"
      : "Account Details";
  const pageSubtitle = isSelfProfileRoute
    ? "Update your profile and password."
    : isEditMode
      ? "Update account information and permissions."
      : "Enter the information below to set up a new system user.";

  return (
    <div className="admin-panel-parent-container">
      <NavBar />
      <TopNavBar />
      <PermissionDialog />
      <div
        className={`account-form-page-wrapper ml-[15%] mt-[66px] px-4 sm:px-6 lg:px-8 py-12 sm:py-6 bg-gray-100 min-h-[calc(100vh-66px)] ${!formEnabled ? "pointer-events-none opacity-60" : ""}`}
      >
        <div className="text-sm mb-4">
          {isSelfProfileRoute ? (
            <>
              <Link
                to="/dashboard"
                className="text-primary no-underline hover:underline"
              >
                Dashboard
              </Link>
              <span className="text-primary mx-2">&gt;</span>
              <span className="text-primary font-bold">My Account</span>
            </>
          ) : (
            <>
              <Link
                to="/manageAccounts"
                className="text-primary no-underline hover:underline"
              >
                Manage Accounts
              </Link>
              <span className="text-primary mx-2">&gt;</span>
              <span className="text-primary font-bold">
                {isEditMode ? "Edit Account" : "Add Account"}
              </span>
            </>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-[#2F7F571A] p-6 sm:p-8 overflow-hidden">
          <div className="mb-8 border-b border-[#2F7F571A] pb-4">
            <h1 className="text-2xl sm:text-3xl font-black text-[#1B1C1C] m-0 mb-2">
              {pageTitle}
            </h1>
            <p className="text-sm font-medium text-[#6F6F6F] m-0">
              {pageSubtitle}
            </p>
          </div>

          {loading ? (
            <p className="text-sm text-primary">Loading account...</p>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="account-form-root max-w-full w-full"
            >
              {/* Left profile photo column hidden — Firebase storage unavailable */}
              {/*
              <div className="lg:w-[220px] flex-shrink-0 min-w-0">
                ... profile photo upload ...
              </div>
              */}

              <div className="w-full min-w-0 max-w-full space-y-5">
                <div className="w-full min-w-0">
                  <label className="block text-sm font-semibold text-[#1B1C1C] mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={fullname}
                    onChange={(e) => setFullname(e.target.value)}
                    placeholder="e.g. Jane Doe"
                    className={inputClass}
                    required
                  />
                </div>

                <div className="w-full min-w-0">
                  <label className="block text-sm font-semibold text-[#1B1C1C] mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="jane.doe@company.com"
                    className={inputClass}
                    required
                  />
                </div>

                {!isEditMode || isEditingSelf ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full min-w-0">
                    <div className="min-w-0">
                      <label className="block text-sm font-semibold text-[#1B1C1C] mb-2">
                        {isEditMode ? "New Password" : "Password"}
                      </label>
                      <div className="relative w-full min-w-0">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="••••••••"
                          className={`${inputClass} pr-10`}
                          required={!isEditMode}
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((v) => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] bg-transparent border-none cursor-pointer p-0"
                        >
                          {showPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="min-w-0">
                      <label className="block text-sm font-semibold text-[#1B1C1C] mb-2">
                        Confirm Password
                      </label>
                      <div className="relative w-full min-w-0">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="••••••••"
                          className={`${inputClass} pr-10`}
                          required={!isEditMode}
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword((v) => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] bg-transparent border-none cursor-pointer p-0"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}

                {isEditMode && isEditingSelf ? (
                  <p className="text-xs text-[#9CA3AF] m-0 -mt-2">
                    Leave password fields blank to keep your current password.
                  </p>
                ) : null}

                <div className="w-full min-w-0">
                  <label className="block text-sm font-semibold text-[#1B1C1C] mb-2">
                    {isEditMode ? "Role" : "System Role"}
                  </label>
                  {isEditMode && isEditingSelf ? (
                    <input
                      type="text"
                      readOnly
                      value={roleLabel(role)}
                      className={`${inputClass} cursor-not-allowed opacity-70`}
                    />
                  ) : (
                    <div className="relative w-full min-w-0">
                      <select
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        className={`${inputClass} appearance-none cursor-pointer pr-10`}
                      >
                        <option value={ROLES.ADMIN}>Admin</option>
                        <option value={ROLES.STAFF}>Staff</option>
                        <option value={ROLES.VIEWER}>Read Only</option>
                      </select>
                      <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#6F6F6F]">
                        ▾
                      </span>
                    </div>
                  )}
                  {isEditMode && isEditingSelf ? (
                    <p className="text-xs text-[#9CA3AF] mt-2 m-0">
                      You cannot change your own role.
                    </p>
                  ) : null}
                </div>

                {!isSelfProfileRoute && !isEditingSelf ? (
                  <div className="w-full min-w-0">
                    <label className="block text-sm font-semibold text-[#1B1C1C] mb-2">
                      Account Status
                    </label>
                    <Tabs
                      value={status}
                      onValueChange={handleStatusTabChange}
                      className="w-full max-w-md"
                    >
                      <TabsList className="account-form-status-tabs w-full h-auto p-1 grid grid-cols-2 gap-1 bg-[#de5d5d]">
                        <TabsTrigger
                          value="active"
                          className="account-form-status-tab account-form-status-tab--active data-[state=active]:bg-[#2E8056] data-[state=active]:text-white cursor-pointer"
                        >
                          Active
                        </TabsTrigger>
                        <TabsTrigger
                          value="inactive"
                          className="account-form-status-tab account-form-status-tab--inactive data-[state=active]:bg-red-200 data-[state=active]:text-white cursor-pointer"
                        >
                          Inactive
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>
                ) : null}

                {!isEditMode ? (
                  <div className="account-form-permissions-box w-full min-w-0">
                    <div className="flex gap-3">
                      <Info className="w-5 h-5 text-[#2F7F57] flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="text-base font-bold text-[#1B1C1C] m-0 mb-2">
                          Account Permissions
                        </p>
                        <p className="text-sm text-[#6F6F6F] font-bold m-0 mb-1">
                          <span className="text-[#1B1C1C]">Admin</span> — Full
                          access to payroll, employees, accounts, invoices, and
                          system management.
                        </p>
                        <p className="text-sm font-bold text-[#6F6F6F] m-0 mb-1">
                          <span className="text-[#1B1C1C]">Staff</span> — Can
                          create and edit employees, payslips, invoices, and
                          metadata. Can archive employees and payslips. Cannot
                          delete records or manage accounts.
                        </p>
                        <p className="text-sm font-bold text-[#6F6F6F] m-0">
                          <span className="text-[#1B1C1C]">Read Only</span> —
                          View-only access to payroll data. Cannot create, edit,
                          or delete records.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : null}

                {isEditMode && accountMeta ? (
                  <div className=" w-full min-w-0">
                    <p className="text-sm font-bold text-[#1B1C1C] m-0 mb-4">
                      Account Information
                    </p>
                    <div className="account-form-meta-row">
                      <div className="account-form-meta-item">
                        <p className="text-xs text-[#6F6F6F] m-0 mb-1">
                          Account ID
                        </p>
                        <p className="text-sm font-semibold text-[#1B1C1C] m-0 whitespace-nowrap">
                          {formatAccountId(accountMeta.accountId)}
                        </p>
                      </div>
                      <div className="account-form-meta-item">
                        <p className="text-xs text-[#6F6F6F] m-0 mb-1">
                          Created On
                        </p>
                        <p className="text-sm font-semibold text-[#1B1C1C] m-0 whitespace-nowrap">
                          {formatDisplayDate(accountMeta.created_on)}
                        </p>
                      </div>
                      <div className="account-form-meta-item">
                        <p className="text-xs text-[#6F6F6F] m-0 mb-1">
                          Last Login
                        </p>
                        <p className="text-sm font-semibold text-[#1B1C1C] m-0 whitespace-nowrap">
                          {formatLastLoginDisplay(accountMeta.last_login)}
                        </p>
                      </div>
                      <div className="account-form-meta-item">
                        <p className="text-xs text-[#6F6F6F] m-0 mb-1">
                          Last Updated
                        </p>
                        <p className="text-sm font-semibold text-[#1B1C1C] m-0 whitespace-nowrap">
                          {formatDisplayDate(accountMeta.last_updated)}
                        </p>
                      </div>
                      <div className="account-form-meta-item">
                        <p className="text-xs text-[#6F6F6F] m-0 mb-1">
                          Created By
                        </p>
                        <p className="text-sm font-semibold text-[#1B1C1C] m-0 whitespace-nowrap">
                          {accountMeta.created_by_name || "—"}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-[#2F7F571A]">
                <Link
                  to="/manageAccounts"
                  className="py-2.5 px-6 rounded-lg text-sm font-semibold border border-[#D1D5DB] bg-white text-[#1B1C1C] no-underline hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </Link>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 py-2.5 px-6 rounded-lg text-sm font-semibold bg-[#2F7F57] text-white border-none cursor-pointer hover:bg-[#256845] transition-colors disabled:opacity-60"
                >
                  {isEditMode ? (
                    <>
                      <Save className="w-4 h-4" />
                      {saving ? "Saving..." : "Save Changes"}
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4" />
                      {saving ? "Creating..." : "Create Account"}
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      <AlertDialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <AlertDialogContent className="z-[9999]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-black">
              Change account status?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-black">
              Are you sure you want to mark this account as&nbsp;
              <span className="font-bold">
                {pendingStatus === "active" ? "Active" : "Inactive"}
              </span>
              ?
              {pendingStatus === "inactive"
                ? " The user will no longer be able to sign in."
                : " The user will regain access to the system."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="text-black"
              onClick={() => setPendingStatus(null)}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-[#2F7F57] hover:bg-[#256845] text-white"
              onClick={confirmStatusChange}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default AccountForm;
